import * as React from "react";
import { Link } from "react-router";

import { Tile, TileHeader } from "@/components/ui/tile";
import { CourseMark } from "@/components/workspace/CourseMark";
import { cn } from "@/lib/utils";

/**
 * The register (ADR 0035): one tile holding rows of at least 46 px on one
 * column template. The tile is the container; nothing per row is boxed; rows
 * separate with the soft line; hover is the well; focus is a 2 px inset ring.
 *
 * The template is a CSS grid track list passed once as `--cols`, so the head
 * and every row share it. A row with one act is one link (`href`); a row that
 * holds a control keeps the grid and makes its title the link. Rows that are
 * not courses (people, work inside a course) omit the mark and code cells and
 * the template says so.
 */
function Register({
  template,
  head,
  as: List = "ul",
  className,
  style,
  children,
  rowsOnly = false,
  ...props
}: Omit<React.HTMLAttributes<HTMLElement>, "children"> & {
  ref?: React.Ref<HTMLElement>;
  /** The grid track list every head and row lays out on. */
  template: string;
  /** A `RegisterHead`, rendered above the rows. */
  head?: React.ReactNode;
  as?: "ul" | "ol";
  children: React.ReactNode;
  /** Compose rows inside a task's existing tile without a second surface. */
  rowsOnly?: boolean;
}) {
  const Frame = rowsOnly ? "section" : Tile;
  return (
    <Frame
      {...(!rowsOnly && { list: true })}
      data-register
      className={cn("overflow-hidden", className)}
      style={{ ...style, "--cols": template } as React.CSSProperties}
      {...props}
    >
      {head}
      <List data-slot="register-rows" className={cn("m-0 list-none p-0", !head && "[&>li:first-child]:border-t-0")}>
        {children}
      </List>
    </Frame>
  );
}

type RegisterHeadProps =
  | {
      /** The module header: an 18/26 title, a count in muted numerals, one quiet act. */
      id: string;
      title: React.ReactNode;
      count?: React.ReactNode;
      action?: React.ReactNode;
      as?: "h2" | "h3";
      className?: string;
      children?: undefined;
    }
  | {
      /** The column-naming row, one cell per track of the template. */
      children: React.ReactNode;
      className?: string;
      id?: undefined;
      title?: undefined;
      count?: undefined;
      action?: undefined;
      as?: undefined;
    };

function RegisterHead(props: RegisterHeadProps) {
  if (props.title !== undefined) {
    const { id, title, count, action, as, className } = props;
    return <TileHeader id={id} title={title} count={count} action={action} as={as} className={className} />;
  }
  return (
    <div
      data-slot="register-head"
      className={cn(ROW_GRID, "px-5 py-2.5 text-meta font-semibold text-muted-foreground", props.className)}
    >
      {props.children}
    </div>
  );
}

const ROW_GRID = "grid min-w-0 grid-cols-[var(--cols)] items-center gap-x-3.5";
const ROW = cn(ROW_GRID, "min-h-[46px] px-5 py-[0.6875rem] text-foreground transition-colors duration-[var(--motion-fast)] aria-[current=page]:bg-primary-wash");
const ROW_WELL = "hover:bg-well";
const ROW_FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

/**
 * One row. With `href` the whole row is one link. With `detail` the row holds
 * what it opened in place (its editor, say): the detail runs under the cells
 * across the full template inside the same `li`, the row keeps its grid, and
 * its title is the link (`RegisterTitle href`), never the row.
 */
function RegisterRow({
  href,
  current,
  detail,
  className,
  children,
  ...props
}: React.ComponentProps<"li"> & {
  /** When set, the whole row is this one link. Ignored beside `detail`. */
  href?: string;
  current?: boolean;
  /** What the row opened in place, rendered under its cells at full width. */
  detail?: React.ReactNode;
}) {
  if (href && !detail) {
    return (
      <li data-slot="register-row" className={cn("border-t border-rule", className)} {...props}>
        <Link to={href} aria-current={current ? "page" : undefined} className={cn(ROW, ROW_WELL, ROW_FOCUS)}>
          {children}
        </Link>
      </li>
    );
  }
  return (
    <li
      data-slot="register-row"
      aria-current={current ? "page" : undefined}
      data-detail={detail ? "" : undefined}
      className={cn("border-t border-rule", ROW, !detail && ROW_WELL, className)}
      {...props}
    >
      {children}
      {detail && (
        <div data-slot="register-row-detail" className="col-span-full min-w-0 border-t border-rule pt-4 mt-[0.6875rem]">
          {detail}
        </div>
      )}
    </li>
  );
}

/** The 20 px mark; only on a row that is a course. */
function RegisterMark({ code, className }: { code: string; className?: string }) {
  return <CourseMark code={code} size="sm" className={className} />;
}

/** The code in its fixed mono column. */
function RegisterCode({ className, children, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="register-code"
      className={cn("whitespace-nowrap font-mono text-meta font-medium tabular-nums text-muted-foreground", className)}
      {...props}
    >
      <bdi>{children}</bdi>
    </span>
  );
}

/** The dominant title, two lines then truncated; the link when the row holds controls. */
function RegisterTitle({
  href,
  link,
  className,
  children,
  ...props
}: React.ComponentProps<"span"> & {
  href?: string;
  /** The link's own attributes when the title is the link: a disclosure's `aria-expanded`, a `ref`, a `className`. */
  link?: Omit<React.ComponentProps<typeof Link>, "to" | "children">;
}) {
  const title = (
    <span
      dir="auto"
      data-slot="register-title"
      className={cn("line-clamp-2 min-w-0 text-body font-semibold text-foreground [overflow-wrap:anywhere]", className)}
      {...props}
    >
      {children}
    </span>
  );
  if (!href) return title;
  const { className: linkClassName, ...linkProps } = link ?? {};
  return (
    <Link
      to={href}
      {...linkProps}
      className={cn("min-w-0 rounded-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", linkClassName)}
    >
      {title}
    </Link>
  );
}

/** The row's one interruption: a 7 px warning dot and warning ink. Empty keeps the track. */
function RegisterNotice({ className, children }: { className?: string; children?: React.ReactNode }) {
  if (!children) return <span data-slot="register-notice" />;
  return (
    <span
      data-slot="register-notice"
      className={cn("flex items-center gap-1.5 whitespace-nowrap text-meta text-status-warning", className)}
    >
      <span aria-hidden="true" className="size-[7px] shrink-0 rounded-full bg-status-warning" />
      {children}
    </span>
  );
}

/** The day column: soft ink for later days, ink at 600 when the day is today. */
function RegisterDay({ today = false, className, children, ...props }: React.ComponentProps<"span"> & { today?: boolean }) {
  return (
    <span
      data-slot="register-day"
      className={cn(
        "whitespace-nowrap text-end text-ui tabular-nums",
        today ? "font-semibold text-foreground" : "text-foreground-soft",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

/** The time column: ink, tabular, right-aligned. */
function RegisterTime({ className, children, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="register-time"
      className={cn("whitespace-nowrap text-end text-ui tabular-nums text-foreground", className)}
      {...props}
    >
      {children}
    </span>
  );
}

export {
  Register,
  RegisterCode,
  RegisterDay,
  RegisterHead,
  RegisterMark,
  RegisterNotice,
  RegisterRow,
  RegisterTime,
  RegisterTitle,
};
